// banking/src/pages/onboarding/corporate/steps/Step2_CompanyAddress.tsx

import React from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { CorporateFormData } from '../CorporateOnboarding';
import USStateSelector from '../components/USStateSelector';
import CountrySelector from '../components/CountrySelector';
import { PHONE_COUNTRY_CODES } from '../utils/phoneCountryCodes';

interface Step2Props {
  formData: CorporateFormData;
  updateFormData: (data: Partial<CorporateFormData>) => void;
  language: Language;
  errors: Record<string, string>;
}

const Step2_CompanyAddress: React.FC<Step2Props> = ({
  formData,
  updateFormData,
  language,
  errors,
}) => {

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {getTranslation('step2_title', language)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {getTranslation('step2_subtitle', language)}
        </p>
      </div>

      {/* Company Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('company_email_label', language)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="email"
          value={formData.companyEmail}
          onChange={(e) => updateFormData({ companyEmail: e.target.value })}
          placeholder="company@example.com"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
            errors.companyEmail ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.companyEmail && (
          <p className="mt-1 text-sm text-red-600">{errors.companyEmail}</p>
        )}
      </div>

      {/* Company Phone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('company_phone_label', language)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {/* Country Code */}
          <div>
            <select
              value={formData.companyPhoneCountryCode}
              onChange={(e) => updateFormData({ companyPhoneCountryCode: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                errors.companyPhoneCountryCode ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{getTranslation('phone_country_code_label', language)}</option>
              {PHONE_COUNTRY_CODES.map((item) => (
                <option key={item.iso2} value={item.code}>
                  {item.code} - {item.country}
                </option>
              ))}
            </select>
          </div>

          {/* Phone Number */}
          <div className="col-span-2">
            <input
              type="tel"
              value={formData.companyPhoneNumber}
              onChange={(e) => updateFormData({ companyPhoneNumber: e.target.value.replace(/\D/g, '') })}
              placeholder={getTranslation('phone_number_label', language)}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                errors.companyPhoneNumber ? 'border-red-500' : 'border-gray-300'
              }`}
            />
          </div>
        </div>
        {(errors.companyPhoneCountryCode || errors.companyPhoneNumber) && (
          <p className="mt-1 text-sm text-red-600">
            {errors.companyPhoneCountryCode || errors.companyPhoneNumber}
          </p>
        )}
      </div>

      {/* Phone Extension (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('phone_extension_label', language)}
        </label>
        <input
          type="text"
          value={formData.companyPhoneExtension || ''}
          onChange={(e) => updateFormData({ companyPhoneExtension: e.target.value })}
          placeholder="1234"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
        />
      </div>

      {/* Address Line 1 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('address_line1_label', language)}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          value={formData.addressLine1}
          onChange={(e) => updateFormData({ addressLine1: e.target.value })}
          placeholder={getTranslation('address_line1_placeholder', language)}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
            errors.addressLine1 ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {errors.addressLine1 && (
          <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          {language === 'es'
            ? 'Incluya calle, número, piso, etc. en un solo campo'
            : language === 'fr'
            ? 'Incluez rue, numéro, étage, etc. dans un seul champ'
            : language === 'de'
            ? 'Geben Sie Straße, Nummer, Etage usw. in einem Feld ein'
            : language === 'it'
            ? 'Includi via, numero, piano, ecc. in un unico campo'
            : language === 'pt'
            ? 'Inclua rua, número, andar, etc. em um único campo'
            : language === 'nl'
            ? 'Voeg straat, nummer, verdieping, enz. toe in één veld'
            : language === 'ru'
            ? 'Укажите улицу, номер, этаж и т.д. в одном поле'
            : language === 'ar'
            ? 'قم بتضمين الشارع والرقم والطابق وما إلى ذلك في حقل واحد'
            : 'Include street, number, floor, etc. in a single field'
          }
        </p>
      </div>

      {/* Country (must come first to determine if US state selector is shown) */}
      <div>
        <CountrySelector
          value={formData.country || ''}
          onChange={(code) => updateFormData({ country: code, state: '' })} // Clear state when country changes
          language={language}
          label={language === 'es'
            ? 'País'
            : language === 'fr'
            ? 'Pays'
            : language === 'de'
            ? 'Land'
            : language === 'it'
            ? 'Paese'
            : language === 'pt'
            ? 'País'
            : language === 'nl'
            ? 'Land'
            : language === 'ru'
            ? 'Страна'
            : language === 'ar'
            ? 'البلد'
            : 'Country'
          }
          required={true}
          error={errors.country}
        />
      </div>

      {/* City, State, Postal Code - Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {language === 'es'
              ? 'Ciudad'
              : language === 'fr'
              ? 'Ville'
              : language === 'de'
              ? 'Stadt'
              : language === 'it'
              ? 'Città'
              : language === 'pt'
              ? 'Cidade'
              : language === 'nl'
              ? 'Stad'
              : language === 'ru'
              ? 'Город'
              : language === 'ar'
              ? 'المدينة'
              : 'City'
            }
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={formData.city || ''}
            onChange={(e) => updateFormData({ city: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
              errors.city ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-600">{errors.city}</p>
          )}
        </div>

        {/* State/Province - Conditional: US State Selector or Text Input */}
        {formData.country === 'US' ? (
          <USStateSelector
            value={formData.state || ''}
            onChange={(code) => updateFormData({ state: code })}
            language={language}
            label={language === 'es'
              ? 'Estado (código ISO-2)'
              : language === 'fr'
              ? 'État (code ISO-2)'
              : language === 'de'
              ? 'Staat (ISO-2-Code)'
              : language === 'it'
              ? 'Stato (codice ISO-2)'
              : language === 'pt'
              ? 'Estado (código ISO-2)'
              : language === 'nl'
              ? 'Staat (ISO-2-code)'
              : language === 'ru'
              ? 'Штат (код ISO-2)'
              : language === 'ar'
              ? 'الولاية (رمز ISO-2)'
              : 'State (ISO-2 code)'
            }
            required={true}
            error={errors.state}
          />
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {language === 'es'
                ? 'Estado/Provincia'
                : language === 'fr'
                ? 'État/Province'
                : language === 'de'
                ? 'Staat/Provinz'
                : language === 'it'
                ? 'Stato/Provincia'
                : language === 'pt'
                ? 'Estado/Província'
                : language === 'nl'
                ? 'Staat/Provincie'
                : language === 'ru'
                ? 'Штат/Провинция'
                : language === 'ar'
                ? 'الولاية/المقاطعة'
                : 'State/Province'
              }
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={formData.state || ''}
              onChange={(e) => updateFormData({ state: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
                errors.state ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.state && (
              <p className="mt-1 text-sm text-red-600">{errors.state}</p>
            )}
          </div>
        )}

        {/* Postal Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {language === 'es'
              ? 'Código Postal'
              : language === 'fr'
              ? 'Code Postal'
              : language === 'de'
              ? 'Postleitzahl'
              : language === 'it'
              ? 'Codice Postale'
              : language === 'pt'
              ? 'Código Postal'
              : language === 'nl'
              ? 'Postcode'
              : language === 'ru'
              ? 'Почтовый индекс'
              : language === 'ar'
              ? 'الرمز البريدي'
              : 'Postal Code'
            }
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            value={formData.postalCode || ''}
            onChange={(e) => updateFormData({ postalCode: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369] ${
              errors.postalCode ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.postalCode && (
            <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>
          )}
        </div>
      </div>

      {/* Website (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {getTranslation('website_label', language)}
        </label>
        <input
          type="url"
          value={formData.website || ''}
          onChange={(e) => updateFormData({ website: e.target.value })}
          placeholder={getTranslation('website_placeholder', language)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B3A369]"
        />
      </div>

      {/* Info box */}
      <div className="bg-[#F5F3E8] border border-[#E5DDB8] rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-[#B3A369]"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-[#9A8B57]">
              {language === 'es'
                ? 'La dirección proporcionada debe ser la dirección registrada de la empresa. Asegúrese de que coincida con sus documentos oficiales.'
                : language === 'fr'
                ? 'L\'adresse fournie doit être l\'adresse enregistrée de l\'entreprise. Assurez-vous qu\'elle correspond à vos documents officiels.'
                : language === 'de'
                ? 'Die angegebene Adresse muss die registrierte Adresse des Unternehmens sein. Stellen Sie sicher, dass sie mit Ihren offiziellen Dokumenten übereinstimmt.'
                : language === 'it'
                ? 'L\'indirizzo fornito deve essere l\'indirizzo registrato dell\'azienda. Assicurati che corrisponda ai tuoi documenti ufficiali.'
                : language === 'pt'
                ? 'O endereço fornecido deve ser o endereço registrado da empresa. Certifique-se de que corresponda aos seus documentos oficiais.'
                : language === 'nl'
                ? 'Het opgegeven adres moet het geregistreerde adres van het bedrijf zijn. Zorg ervoor dat het overeenkomt met uw officiële documenten.'
                : language === 'ru'
                ? 'Указанный адрес должен быть зарегистрированным адресом компании. Убедитесь, что он совпадает с вашими официальными документами.'
                : language === 'ar'
                ? 'يجب أن يكون العنوان المقدم هو العنوان المسجل للشركة. تأكد من أنه يطابق مستنداتك الرسمية.'
                : 'The address provided should be the registered address of the company. Make sure it matches your official documents.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2_CompanyAddress;
